import { metadataFromLocalStorage } from '@/utils/LocalStorage';

import { apiClient } from '../apiClients';
import { Endpoints } from '../../network';
import { IResponse } from '../../utils/interfaces';
import {
  IGetSubscriptionInfo,
  IPlan,
  IPlansOffer,
  IPlansParams,
  IProductAccessCheck,
  IStartSubscription,
  IStartSubscriptionBody,
  ISubscribeProductBody,
  IUserConsumable,
  IVerifyResponse,
  IVerifySubscriptionPayload,
} from '../../utils/interfaces/Subscription';

export const handleSubscribe = async (email: string) => {
  const response = await apiClient.post(`${Endpoints.subscribe}`, {
    email: email,
  });

  return response?.data;
};

export const plans = async (
  params?: IPlansParams,
): Promise<IResponse<IPlan[]>> => {
  const response = await apiClient.get(`${Endpoints.plans}`, { params });

  return response?.data;
};

export const startSubscription = async (
  data: IStartSubscriptionBody,
): Promise<IResponse<IStartSubscription>> => {
  const metadata = metadataFromLocalStorage();
  const response = await apiClient.post(`${Endpoints.startSubscription}`, {
    ...data,
    ...(metadata ? { meta_data: metadata } : {}),
  });

  return response?.data;
};

export const verifySubscription = async (
  data: IVerifySubscriptionPayload,
): Promise<IResponse<IVerifyResponse>> => {
  const response = await apiClient.post(
    `${Endpoints.verifySubscription}`,
    data,
  );

  return response?.data;
};

export const applySubscriptionOffer = async (data: {
  plan_id: number;
  offer_id?: number;
  payment_method: string;
  code?: string;
  coupon_code?: string;
}): Promise<IResponse<any>> => {
  const response = await apiClient.post(
    `${Endpoints.applySubscriptionOffer}`,
    data,
  );

  return response?.data;
};

export const userConsumable = async (): Promise<IResponse<IUserConsumable>> => {
  const response = await apiClient.get(`${Endpoints.userConsumable}`);

  return response?.data;
};

export const subscribeProduct = async (
  data: ISubscribeProductBody,
): Promise<IResponse<any>> => {
  const response = await apiClient.post(`${Endpoints.subscribeProduct}`, data);

  return response?.data;
};

export const checkSubscription = async (
  razorpay_subscription_id: string,
): Promise<IResponse<any>> => {
  const response = await apiClient.get(`${Endpoints.checkSubscription}`, {
    params: {
      razorpay_subscription_id,
    },
  });

  return response?.data;
};
export const plansOffer = async (): Promise<IResponse<IPlansOffer>> => {
  const response = await apiClient.get(`${Endpoints.plansOffer}`);

  return response?.data;
};

export const subscriptionCancel = async (): Promise<IResponse<any>> => {
  const response = await apiClient.post(Endpoints.subscriptionCancel);

  return response?.data;
};

export const getSubscriptionInfo = async (
  id: string,
): Promise<IResponse<IGetSubscriptionInfo>> => {
  const hashKey = encodeURIComponent(id as string);
  const response = await apiClient.get(
    `${Endpoints.getSubscriptionInfo}/${hashKey}`,
  );
  return response?.data;
};

export const subscriptionPaymentVerify = async (
  rp_payment_id: string,
  hash: string,
) => {
  try {
    const hashKey = encodeURIComponent(hash);
    const response = await apiClient.post(
      `${Endpoints.subscriptionPaymentVerify}`,
      { rp_payment_id, hase: hashKey },
    );
    return response?.data ?? null;
  } catch (error) {
    return null;
  }
};

export const productAccessCheck = async (
  productId: string,
): Promise<IResponse<IProductAccessCheck>> => {
  const response = await apiClient.get(Endpoints.productAccessCheck(productId));
  return response?.data ?? null;
};

export const productAccessCheckSSR = async (
  productId: string,
  token: string,
): Promise<IResponse<IProductAccessCheck>> => {
  const urlPath = Endpoints.productAccessCheck(productId);
  const fullUrl = `${process.env.NEXT_PUBLIC_API_URL}${urlPath}`;

  try {
    const response = await fetch(fullUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Error fetching product access: ${response.statusText} `);
    }

    const data = await response.json();

    return data ?? null;
  } catch (error) {
    console.error('Fetch error in productAccessCheckSSR:', error);
    throw error;
  }
};

export const productAccessEnroll = async (
  productId: string,
): Promise<IResponse<any>> => {
  const response = await apiClient.post(
    Endpoints.productAccessEnroll(productId),
  );
  return response?.data ?? null;
};
